<template>
  <v-card class="dt-mvp-card" elevation="1" ref="dtCard">
    <!-- ── Toolbar with twin selector ──────────────────────────────────── -->
    <v-toolbar color="primary" density="compact" class="pr-3">
      <DigitalTwinSelector
        v-if="!isLoading && siteSystemTabs.length"
        :digitalTwins="siteSystemTabs as IDigitalTwin[]"
        :modelValue="currentDigitalTwinId as number"
        :siteSystemLabels="siteSystemLabels"
        @update:modelValue="onTabSelect"
      />
      <v-progress-circular
        v-else-if="isLoading"
        indeterminate
        color="white"
        size="20"
        class="ml-4"
      />
      <span v-else class="text-white text-body-2 font-italic ml-4">
        No Digital Twin set up for this site yet.
      </span>
      <v-spacer />
      <template v-if="isManagement">
        <template v-if="isEditing">
          <v-btn
            icon
            density="compact"
            variant="text"
            color="white"
            :disabled="!canUndo"
            title="Undo (Cmd+Z)"
            @click="undoAction"
          >
            <v-icon>mdi-undo</v-icon>
          </v-btn>
          <v-btn
            icon
            density="compact"
            variant="text"
            color="white"
            :disabled="!canRedo"
            title="Redo (Cmd+Shift+Z)"
            @click="redoAction"
          >
            <v-icon>mdi-redo</v-icon>
          </v-btn>
          <v-divider vertical class="mx-2" />
          <v-btn
            color="white"
            :disabled="!isDirty"
            title="Reset to last saved state"
            prepend-icon="mdi-restore"
            @click="resetAction"
          >
            Reset
          </v-btn>
          <v-btn
            size="x-large"
            color="white"
            :disabled="!isDirty || isSaving"
            :loading="isSaving"
            title="Save changes"
            @click="saveAction"
          >
            Save
          </v-btn>
          <v-divider vertical class="mx-2" />
          <template v-if="currentDigitalTwin">
            <v-btn
              color="white"
              variant="text"
              prepend-icon="mdi-pencil-box-outline"
              title="Edit current Digital Twin"
              @click="showEditModal = true"
            >
              Edit Digital Twin
            </v-btn>
          </template>
        </template>
        <!-- Auto-play button — view mode only, requires at least 2 twins -->
        <v-btn
          v-if="!isEditing && digitalTwins.length > 1"
          :color="isAutoPlay ? 'yellow-darken-2' : 'white'"
          variant="text"
          :prepend-icon="isAutoPlay ? 'mdi-pause' : 'mdi-play'"
          :title="isAutoPlay ? 'Stop auto-play' : 'Auto-play tabs'"
          @click="toggleAutoPlay"
        >
          {{ isAutoPlay ? 'Stop' : 'Auto-play' }}
        </v-btn>
        <v-btn
          color="white"
          variant="text"
          :prepend-icon="isEditing ? 'mdi-eye-outline' : 'mdi-pencil-outline'"
          :title="
            isEditing ? 'Switch to view-only mode' : 'Switch to edit mode'
          "
          @click="isViewOnly = !isViewOnly"
        >
          {{ isEditing ? 'View Only' : 'Edit' }}
        </v-btn>
      </template>
      <v-btn
        v-if="!isManagement && digitalTwins.length > 1"
        :color="isAutoPlay ? 'yellow-darken-2' : 'white'"
        variant="text"
        :prepend-icon="isAutoPlay ? 'mdi-pause' : 'mdi-play'"
        :title="isAutoPlay ? 'Stop auto-play' : 'Auto-play tabs'"
        @click="toggleAutoPlay"
      >
        {{ isAutoPlay ? 'Stop' : 'Auto-play' }}
      </v-btn>
      <v-btn
        v-if="!isManagement"
        color="white"
        variant="flat"
        :append-icon="isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"
        :ripple="false"
        :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
        @click="toggleFullscreen"
      >
        <span class="mr-1" :class="{ 'mr-2': isFullscreen }">
          {{ isFullscreen ? 'Exit' : 'Fullscreen' }}
        </span>
      </v-btn>
    </v-toolbar>

    <!-- ── Item palette (edit mode) ────────────────────────────────────── -->
    <ItemPalette
      v-if="isEditing && currentDigitalTwin"
      :equipmentGroups="equipmentGroups"
      :placedEquipmentIds="placedEquipmentIds"
      :currentSiteSystemId="currentDigitalTwin?.siteSystemId ?? null"
    />

    <!-- ── Body: canvas ──────────────────────────────────────────────────── -->
    <div class="dt-mvp-body">
      <!-- Right: Base SVG Canvas with overlay -->
      <div class="canvas-pane">
        <BaseSvgCanvas
          v-if="currentDigitalTwin && currentDigitalTwin.baseLayout"
          :key="currentDigitalTwinId ?? undefined"
          :baseLayout="currentDigitalTwin.baseLayout"
          :overlay="currentOverlay as IOverlayItem[]"
          :segments="currentSegments"
          :selectedIds="selectedOverlayItemIds as string[]"
          :readonly="!isEditing"
          :equipmentReadingsMap="equipmentReadings"
          @remove="removeOverlayItem"
          @move="
            (id: string, x: number, y: number) => moveOverlayItem(id, x, y)
          "
          @moveStart="() => commitSnapshot()"
          @resizeStart="() => commitSnapshot()"
          @resize="
            (id: string, scale: number) => resizeOverlayItem(id, scale)
          "
          @select="onItemSelect"
          @selectMultiple="(ids: string[]) => selectItems(ids)"
          @drop="onDrop"
          @segmentsChange="onSegmentsChange"
        />

        <!-- Empty state while loading / no twin selected -->
        <div v-else class="canvas-empty">
          <v-progress-circular v-if="isLoading" indeterminate color="primary" />
          <span v-else class="text-medium-emphasis text-body-2">
            {{ canvasEmptyMessage }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Edit Digital Twin modal ─────────────────────────────────────── -->
    <DigitalTwinFormModal
      v-if="isManagement"
      v-model="showEditModal"
      :siteId="siteId"
      :digitalTwin="currentDigitalTwin"
      @saved="load(siteId)"
    />

    <!-- ── Unsaved changes confirmation ───────────────────────────────── -->
    <v-dialog v-model="showUnsavedModal" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-6 pb-2">Unsaved Changes</v-card-title>
        <v-card-text class="pa-6 pt-2">
          You have unsaved changes on this Digital Twin. Proceed anyway?
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn variant="text" color="primary" @click="cancelTabSwitch"
            >No</v-btn
          >
          <v-btn variant="flat" color="primary" @click="confirmTabSwitch"
            >Yes</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import DigitalTwinSelector from '../Components/DigitalTwinSelector.vue';
import ItemPalette from '../Components/ItemPalette.vue';
import BaseSvgCanvas from '../Components/BaseSvgCanvas.vue';
import DigitalTwinFormModal from '../Components/DigitalTwinFormModal.vue';

import { useDigitalTwinMVP } from '../Composables/useDigitalTwinMVP';
import { getDigitalTwinSites } from '../API';

import type {
  IDigitalTwin,
  IOverlayItem,
  IEquipment,
  IEquipmentReading,
} from '../types';

const route = useRoute();
const router = useRouter();
const siteId = computed(() => Number(route.params.siteId));
const isManagement = computed(() => route.name === 'DigitalTwinMVPManagement');
const isViewOnly = ref(false);
const isEditing = computed(() => isManagement.value && !isViewOnly.value);

const showEditModal = ref(false);
const showUnsavedModal = ref(false);
const pendingTabId = ref<number | null>(null);

// ── Auto-play ─────────────────────────────────────────────────────────────
const AUTO_PLAY_INTERVAL_MS = 60_000;
const isAutoPlay = ref(false);
let autoPlayTimer: ReturnType<typeof setInterval> | null = null;

const startAutoPlay = (): void => {
  autoPlayTimer = setInterval(() => {
    const twins = digitalTwins.value;
    if (twins.length < 2) return;
    const idx = twins.findIndex((dt) => dt.id === currentDigitalTwinId.value);
    const next = twins[(idx + 1) % twins.length];
    selectDigitalTwin(next.id);
  }, AUTO_PLAY_INTERVAL_MS);
};

const stopAutoPlay = (): void => {
  if (autoPlayTimer !== null) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

const toggleAutoPlay = (): void => {
  isAutoPlay.value = !isAutoPlay.value;
  if (isAutoPlay.value) {
    startAutoPlay();
  } else {
    stopAutoPlay();
  }
};
// ─────────────────────────────────────────────────────────────────────────

// ── Fullscreen ────────────────────────────────────────────────────────────
const dtCard =
  useTemplateRef<InstanceType<typeof import('vuetify/components').VCard>>(
    'dtCard'
  );
const isFullscreen = ref(false);

const toggleFullscreen = async (): Promise<void> => {
  const el = dtCard.value;
  if (!el) return;
  try {
    if (!document.fullscreenElement) {
      await (el.$el as HTMLElement).requestFullscreen();
      isFullscreen.value = true;
    } else {
      await document.exitFullscreen();
      isFullscreen.value = false;
    }
  } catch (err) {
    console.error(`Error toggling fullscreen: ${err}`);
  }
};

const handleFullscreenChange = (): void => {
  isFullscreen.value = !!document.fullscreenElement;
};
// ─────────────────────────────────────────────────────────────────────────

const {
  digitalTwins,
  siteSystemTabs,
  currentDigitalTwinId,
  currentDigitalTwin,
  currentOverlay,
  currentSegments,
  onSegmentsChange,
  equipmentGroups,
  equipmentReadings,
  isLoading,
  selectedOverlayItemIds,
  load,
  loadAllReadings,
  fetchReadingsForItem,
  selectDigitalTwin,
  selectItem,
  selectItems,
  addOverlayItem,
  removeOverlayItem,
  removeSelectedItems,
  moveOverlayItem,
  resizeOverlayItem,
  commitSnapshot,
  canUndo,
  canRedo,
  undoAction,
  redoAction,
  isDirty,
  isSaving,
  saveAction,
  resetAction,
} = useDigitalTwinMVP();

/** IDs of equipment already placed on any twin for this site — passed to ItemPalette for filtering. */
const placedEquipmentIds = computed<number[]>(() =>
  (digitalTwins.value as IDigitalTwin[]).flatMap((dt) =>
    dt.overlay.map((o) => o.itemId)
  )
);

/** Map of siteSystemId → siteSystemName for tab labels, derived from equipment groups. */
const siteSystemLabels = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {};
  for (const g of equipmentGroups.value) {
    map[g.siteSystemId] = g.siteSystemName;
  }
  return map;
});

/** Message for the canvas empty-state, depending on whether the active tab is a real twin. */
const canvasEmptyMessage = computed<string>(() => {
  // A selected twin with no layout at all. Placeholders carry a blank SVG, so
  // this is only a fallback if that default is missing.
  if (currentDigitalTwin.value && !currentDigitalTwin.value.baseLayout) {
    return 'No base layout for this site system yet.';
  }
  if (siteSystemTabs.value.length) return 'No digital twin selected.';
  return "This site doesn't have a Digital Twin yet.";
});

function onTabSelect(id: number): void {
  // Manual tab switch cancels auto-play
  if (isAutoPlay.value) {
    isAutoPlay.value = false;
    stopAutoPlay();
  }
  if (isDirty.value) {
    pendingTabId.value = id;
    showUnsavedModal.value = true;
  } else {
    selectDigitalTwin(id);
  }
}

function confirmTabSwitch(): void {
  if (pendingTabId.value !== null) {
    selectDigitalTwin(pendingTabId.value);
    pendingTabId.value = null;
  }
  showUnsavedModal.value = false;
}

function cancelTabSwitch(): void {
  pendingTabId.value = null;
  showUnsavedModal.value = false;
}

let readingsPollingTimer: ReturnType<typeof setInterval> | null = null;

const startReadingsPolling = (): void => {
  readingsPollingTimer = setInterval(async () => {
    await loadAllReadings();
    route.meta.isRealTimeReport = true;
  }, 60_000);
};

const stopReadingsPolling = (): void => {
  if (readingsPollingTimer !== null) {
    clearInterval(readingsPollingTimer);
    readingsPollingTimer = null;
  }
};

/**
 * Decide what to do once a site's twins have loaded.
 *
 * A site with no Digital Twin is still valid to stay on, as long as it has site
 * systems — i.e. it appears in the Digital Twin sites dropdown — so the user can
 * set one up. We only bounce back to the admin index when the site has nothing
 * to manage at all (no twin AND not in the dropdown).
 *
 * @returns true when the user stays on the page, false when redirected away.
 */
async function resolveAfterLoad(id: number): Promise<boolean> {
  if (digitalTwins.value.length === 0) {
    const dropdownSites = await getDigitalTwinSites().catch(() => []);
    const hasSiteSystem = dropdownSites.some(
      (s) => s.id === id || s.siteId === id
    );
    if (!hasSiteSystem) {
      router.push({ name: 'DigitalTwinMVPAdminIndex' });
      return false;
    }
    return true;
  }
  try {
    await loadAllReadings();
    route.meta.isRealTimeReport = true;
  } catch {
    route.meta.isRealTimeReport = false;
  }
  return true;
}

watch(siteId, async (newId) => {
  await load(newId);
  await resolveAfterLoad(newId);
});

onMounted(async () => {
  await load(siteId.value);
  const staying = await resolveAfterLoad(siteId.value);
  if (!staying) return;
  startReadingsPolling();
  window.addEventListener('keydown', handleKeydown);
  document.addEventListener('fullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  route.meta.isRealTimeReport = false;
  stopReadingsPolling();
  stopAutoPlay();
  window.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
});

function handleKeydown(e: KeyboardEvent): void {
  if (!isEditing.value) return;

  // Delete / Backspace — remove all selected overlay items (skip when typing in an input)
  if (e.key === 'Delete' || e.key === 'Backspace') {
    const t = e.target as HTMLElement;
    const isInput = t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
    if (!isInput && selectedOverlayItemIds.value.length > 0) {
      e.preventDefault();
      removeSelectedItems();
      return;
    }
  }

  const isMod = e.ctrlKey || e.metaKey;
  if (!isMod) return;
  if (e.key === 'z' && !e.shiftKey) {
    e.preventDefault();
    undoAction();
  } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
    e.preventDefault();
    redoAction();
  }
}

const onItemSelect = (id: string | null, multi: boolean): void => {
  selectItem(id, multi);
};

const onDrop = (payload: {
  equipment: IEquipment;
  siteSystemId?: number;
  siteSystemName?: string;
  readings?: IEquipmentReading[];
  x: number;
  y: number;
}): void => {
  addOverlayItem(
    payload.equipment,
    payload.x,
    payload.y,
    payload.siteSystemId,
    payload.siteSystemName
  );
  fetchReadingsForItem(payload.equipment.equipmentId);
};
</script>

<style lang="scss" scoped>
.dt-mvp-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px); // fits within the Controls shell
  min-height: 480px;

  &:fullscreen {
    height: 100vh;
    min-height: 100vh;
    border-radius: 0;
  }
}

.dt-mvp-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; // allow flex children to shrink below content size
  position: relative; // anchor for the absolute peer drawer
  overflow: hidden; // clip drawer to canvas area
}

.canvas-pane {
  flex: 1;
  min-width: 0;
  height: 100%;
}

.canvas-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
